import { TableDataProviderConfig } from "./types"
import { FeatureFlagName } from "../../../mp-utils/generic-utils/feature-flags/types"

export const TABLE_DATA_PROVIDER_CONFIG: Record<
  string,
  Record<string, TableDataProviderConfig>
> = {
  organization: {
    states: { dataSource: "cube" },
    districts: { dataSource: "cube" },
    schools: { dataSource: "cube" },
    classes: { dataSource: "cube" },
    admins: { dataSource: "cube" },
    teachers: { dataSource: "cube" },
    students: { dataSource: "cube" },
    "license-pools": { dataSource: "graphql" },
    "license-assign": { dataSource: "graphql" }
  },
  insights: {
    // All insights tables use Cube.js
    "usage-monthly": { dataSource: "cube" },
    "details-usage": { dataSource: "cube" },
    "activity-log": { dataSource: "cube" },
    scores: { dataSource: "cube" },
    "performance-report": { dataSource: "graphql" },
    "custom-reports": {
      dataSource: "graphql",
      type: "view",
      viewName: "customReports"
    },
    "custom-reports-view": {
      dataSource: "graphql",
      type: "view",
      viewName: "customReports.view"
    }
  },
  performance: {
    // All performance tables use Cube.js
    goals: { dataSource: "cube" },
    "goals-score-range-by-test": { dataSource: "cube" },
    "goals-score-range-by-subject": { dataSource: "cube" }
  },
  content: {
    // All content tables use Cube.js
    "content-library/element": { dataSource: "cube" },
    "content-library/study-hall": { dataSource: "cube" },
    "content-library/practice-test": { dataSource: "cube" },
    "content-library/mastery-lesson": { dataSource: "cube" },
    "content-library/mastery-curriculum": { dataSource: "cube" }
  },
  "teaching-tools": {
    assignments: { dataSource: "graphql" },
    "assignment-record-students": { dataSource: "graphql" }
  },
  "assignments-drawer": {
    content: { dataSource: "cube" },
    students: { dataSource: "graphql" },
    classes: { dataSource: "graphql" }
    // segments: { dataSource: "cube" }
  }
}

// Map frontend column names to backend field names
export const SORT_FIELD_MAPPING = {
  // Student fields
  "org_view_students.studentFullname": "fullName",
  "org_view_students.studentId": "id",
  "org_view_students.studentFirstName": "firstName",
  "org_view_students.studentLastName": "lastName",
  "org_view_students.grade": "grade",
  "org_view_students.classesCount": "classesCount",
  "org_view_students.hasActiveLicense": "hasActiveLicense",
  "org_view_students.email": "email",
  "org_view_students.studentUserName": "username",
  // State fields
  "org_view_states.code": "code",
  "org_view_states.state": "state",
  "org_view_states.isActive": "isActive",
  "org_view_states.districtCount": "districtCount",
  "org_view_states.schoolsCount": "schoolsCount",
  "org_view_states.activeStudentsCount": "activeStudentsCount",
  "org_view_states.averageTimeSpent": "averageTimeSpent",
  // District fields
  "org_view_districts.districtId": "districtId",
  "org_view_districts.districtName": "districtName",
  "org_view_districts.state": "state",
  "org_view_districts.isActive": "isActive",
  "org_view_districts.districtAdminsCount": "districtAdminsCount",
  "org_view_districts.schoolsCount": "schoolsCount",
  "org_view_districts.schoolAdminsCount": "schoolAdminsCount",
  "org_view_districts.teachersCount": "teachersCount",
  "org_view_districts.activeStudentsCount": "activeStudentsCount",
  "org_view_districts.averageTimeSpent": "averageTimeSpent",
  // School fields
  "org_view_schools.id": "id",
  "org_view_schools.schoolId": "schoolId",
  "org_view_schools.name": "name",
  "org_view_schools.isActive": "isActive",
  "org_view_schools.adminsCount": "schoolAdmins",
  "org_view_schools.teachersCount": "teachers",
  "org_view_schools.studentsCount": "students",
  "org_view_schools.activeStudentsCount": "activeStudents",
  "org_view_schools.averageTimeSpent": "averageTimeSpent",
  // Admin fields
  "org_view_admins.id": "id",
  "org_view_admins.fullName": "fullName",
  "org_view_admins.role": "role",
  "org_view_admins.schoolName": "schoolName",
  "org_view_admins.isActive": "isActive",
  "org_view_admins.lastActive": "lastActive",
  "org_view_admins.state": "state",
  "org_view_admins.email": "email",
  "org_view_admins.districtName": "districtName",
  // Teacher fields
  "org_view_teachers.id": "id",
  "org_view_teachers.fullName": "fullName",
  "org_view_teachers.schoolName": "schoolName",
  "org_view_teachers.classesCount": "classesCount",
  "org_view_teachers.isActive": "isActive",
  "org_view_teachers.teacherLastActive": "teacherLastActive",
  "org_view_teachers.districtName": "districtName",
  "org_view_teachers.state": "state",
  "org_view_teachers.email": "email",
  // License pool fields
  "org_view_licenses.id": "id",
  "org_view_licenses.orgType": "orgType",
  "org_view_licenses.maxLicenses": "seatCount",
  "org_view_licenses.startDate": "startDate",
  "org_view_licenses.endDate": "endDate",
  // Class fields
  "org_view_classes.id": "id",
  "org_view_classes.classId": "id",
  "org_view_classes.name": "name",
  "org_view_classes.studentsCount": "studentsCount",
  "org_view_classes.activeStudentsCount": "activeStudentsCount",
  "org_view_classes.period": "period",
  "org_view_classes.subject": "subject",
  "org_view_classes.schoolName": "schoolName",
  "org_view_classes.districtName": "districtName",
  "org_view_classes.state": "state",
  "org_view_classes.eocSubjects": "eocSubjects",
  "org_view_classes.teacherName": "teacherName",
  "org_view_classes.averageTimeSpent": "averageTimeSpent",
  // Assignments
  "assignments.id": "id",
  "assignments.name": "name",
  "assignments.assignee": "assignees",
  "assignments.dueDate": "due",
  "assignments.openDate": "open",
  "assignments.closeDate": "close",
  "assignments.completedPercent": "percentComplete",
  "assignments.averageScore": "averageScore",
  "assignments.assignmentStatus": "status",
  // Assignment record drill-in fields
  "assignment_record_students.studentName": "studentName",
  "assignment_record_students.classNames": "classNames",
  "assignment_record_students.status": "status",
  "assignment_record_students.completedAt": "completedAt",
  "assignment_record_students.percentComplete": "percentComplete",
  "assignment_record_students.accuracy": "accuracy"
}

export const PROPERTY_FILTERS_MAPPING = {
  organization: "ORGANIZATION",
  district: "DISTRICT",
  teacher: "TEACHER",
  student: "STUDENT",
  class: "CLASS",
  school: "SCHOOL",
  contentType: "CONTENT_TYPE"
}

// default id field for table subpage (this matches into the table records)
export const GRAPHQL_ID_PROP_BY_SUBPAGE: Record<string, string> = {
  states: "org_view_states.code",
  districts: "org_view_districts.districtId",
  schools: "org_view_schools.id",
  admins: "org_view_admins.id",
  teachers: "org_view_teachers.id",
  students: "org_view_students.studentId",
  "license-assign": "org_view_students.studentId",
  classes: "org_view_classes.id",
  "license-pools": "org_view_licenses.id",
  assignments: "assignments.id",
  "assignment-record-students": "assignment_record_students.studentId",
  "students-usage": "usage_students.studentId",
  "teachers-usage": "usage_teachers.teacherId",
  scores: "scores_view.id"
}

export const TABLE_DATA_PROVIDER_CONFIG_BY_FEATURE_FLAG = {
  organization: {
    states: {
      featureFlag: FeatureFlagName.reporting_graphql_organization_states,
      dataSource: "graphql",
      type: "view",
      viewName: "orgViewStates"
    },
    districts: {
      featureFlag: FeatureFlagName.reporting_graphql_organization_districts,
      dataSource: "graphql",
      type: "view",
      viewName: "orgViewDistricts"
    },
    schools: {
      featureFlag: FeatureFlagName.reporting_graphql_organization_schools,
      dataSource: "graphql",
      type: "view",
      viewName: "orgViewSchools"
    },
    classes: {
      featureFlag: FeatureFlagName.reporting_graphql_organization_classes,
      dataSource: "graphql",
      type: "view",
      viewName: "orgViewClasses"
    },
    admins: {
      featureFlag: FeatureFlagName.reporting_graphql_organization_admins,
      dataSource: "graphql",
      type: "view",
      viewName: "orgViewAdmins"
    },
    teachers: {
      featureFlag: FeatureFlagName.reporting_graphql_organization_teachers,
      dataSource: "graphql",
      type: "view",
      viewName: "orgViewTeachers"
    },
    students: {
      featureFlag: FeatureFlagName.reporting_graphql_organization_students,
      dataSource: "graphql",
      type: "view",
      viewName: "orgViewStudents"
    }
  },
  insights: {
    "students-usage": {
      featureFlag: FeatureFlagName.reporting_graphql_usage_students,
      dataSource: "graphql",
      type: "view",
      viewName: "studentUsage"
    },
    "teachers-usage": {
      featureFlag: FeatureFlagName.reporting_graphql_usage_teachers,
      dataSource: "graphql",
      type: "view",
      viewName: "teacherUsage"
    },
    "activity-log": {
      featureFlag: FeatureFlagName.reporting_graphql_usage_activity_log,
      dataSource: "graphql",
      type: "view",
      viewName: "activityLog"
    },
    "details-usage": {
      featureFlag: FeatureFlagName.reporting_graphql_usage_activity_log,
      dataSource: "graphql",
      type: "view",
      viewName: "activityLog"
    },
    scores: {
      featureFlag: FeatureFlagName.reporting_graphql_performance_scores,
      dataSource: "graphql",
      type: "view",
      viewName: "performanceTestScores"
    },
    "subtests-scores": {
      featureFlag: FeatureFlagName.reporting_graphql_performance_scores,
      dataSource: "graphql",
      type: "view",
      viewName: "performanceScores"
    },
    questions: {
      featureFlag: FeatureFlagName.reporting_graphql_performance_questions,
      dataSource: "graphql",
      type: "view",
      viewName: "performanceQuestions"
    }
  },
  content: {
    "content-library": {
      featureFlag: FeatureFlagName.reporting_graphql_educator_content_library,
      dataSource: "graphql",
      type: "view",
      viewName: "educatorContentLibrary"
    },
    "content-library/practice-test": {
      featureFlag: FeatureFlagName.reporting_graphql_educator_content_library,
      dataSource: "graphql",
      type: "view",
      viewName: "educatorContentLibrary.PracticeTest"
    },
    "content-library/mastery-curriculum": {
      featureFlag: FeatureFlagName.reporting_graphql_educator_content_library,
      dataSource: "graphql",
      type: "view",
      viewName: "educatorContentLibrary.BookChapter"
    },
    "content-library/mastery-lesson": {
      featureFlag: FeatureFlagName.reporting_graphql_educator_content_library,
      dataSource: "graphql",
      type: "view",
      viewName: "educatorContentLibrary.OnlineLesson"
    },
    "content-library/element": {
      featureFlag: FeatureFlagName.reporting_graphql_educator_content_library,
      dataSource: "graphql",
      type: "view",
      viewName: "educatorContentLibrary.BellRinger"
    },
    "content-library/study-hall": {
      featureFlag: FeatureFlagName.reporting_graphql_educator_content_library,
      dataSource: "graphql",
      type: "view",
      viewName: "educatorContentLibrary.SkillReview"
    }
  }
}
